/**
 * Shift-category helpers (#1605) — read the server-stamped flags first.
 *
 * The `/me/schedule` assignment rows now carry `kind` (am|pm|oncall|
 * other), `is_oncall` and `is_weekend`, stamped server-side by the ONE
 * shared classifier (`backend/src/scheduler/types/shift_category.py`).
 * Every member surface reads those flags through these helpers instead
 * of re-deriving categories client-side.
 *
 * Why the fallbacks exist: the flags are additive optional fields, so a
 * PWA-cached pre-#1605 payload has them `undefined`. In that (transient)
 * case we fall back to the legacy client derivations — the session-label
 * substring for on-call (the pre-#1605 behaviour, which only ever
 * matched seed/demo rows since live solver writes leave `session` NULL)
 * and a UTC weekday parse for weekends. Once the cache refreshes, the
 * server flags win everywhere.
 */

/** The structural slice of a schedule row these helpers need. */
export interface ShiftCategoryRow {
  date: string;
  session?: string | null;
  kind?: string | null;
  is_oncall?: boolean | null;
  is_weekend?: boolean | null;
}

export type ShiftKind = "am" | "pm" | "oncall" | "other";

/** Legacy client heuristic — kept ONLY as the cached-payload fallback. */
function sessionHintsOnCall(session: string | null | undefined): boolean {
  const lowered = (session ?? "").toLowerCase();
  return lowered.includes("on-call") || lowered === "oncall";
}

/** A YYYY-MM-DD ISO date is a weekend iff it lands on Sat/Sun (UTC parse
 *  so the classification is viewer-timezone-independent). */
export function isWeekendIsoDate(iso: string): boolean {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

/** Is this row an on-call shift? Server flag first, legacy fallback. */
export function isOnCallRow(row: ShiftCategoryRow): boolean {
  if (typeof row.is_oncall === "boolean") return row.is_oncall;
  return sessionHintsOnCall(row.session);
}

/** Is this row on a weekend day? Server flag first, date fallback. */
export function isWeekendRow(row: ShiftCategoryRow): boolean {
  if (typeof row.is_weekend === "boolean") return row.is_weekend;
  return isWeekendIsoDate(row.date);
}

/** The row's category bucket. Server `kind` first; the fallback derives
 *  on-call via `isOnCallRow` and AM/PM from a literal session label. */
export function rowKind(row: ShiftCategoryRow): ShiftKind {
  if (row.kind === "am" || row.kind === "pm" || row.kind === "oncall" || row.kind === "other") {
    return row.kind;
  }
  if (isOnCallRow(row)) return "oncall";
  const sess = (row.session ?? "").trim().toUpperCase();
  if (sess === "AM") return "am";
  if (sess === "PM") return "pm";
  return "other";
}

/** The /me week-browser filter vocabulary (#1605). */
export type ShiftCategoryFilter = "all" | "weekends" | "oncall" | "am" | "pm";

/** Does a row pass the active filter chip? */
export function matchesShiftFilter(row: ShiftCategoryRow, filter: ShiftCategoryFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "weekends":
      return isWeekendRow(row);
    case "oncall":
      return isOnCallRow(row);
    case "am":
      return rowKind(row) === "am";
    case "pm":
      return rowKind(row) === "pm";
    default:
      return true;
  }
}
